import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  OnInit,
  Output,
  TemplateRef,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';

import { createUniqueElementId } from '@kros-sk/shared/utils-fn/dom-utils';
import { DeviceDetectorServiceOld, MobileHeightDirective, MobileHeightSettings } from '@kros-sk/device-detector';
import { NEW_DESIGN } from '@kros-sk/core/design-version';

import { KrosModalRef, ModalContent } from '../services/kros-modal-ref';

@Component({
  selector: 'kros-modal',
  templateUrl: './kros-modal.component.html',
  styleUrls: ['./kros-modal.component.scss'],
  imports: [CommonModule, A11yModule, MobileHeightDirective, TranslateModule],
})
export class KrosModalComponent implements OnInit {
  private modalRef = inject(KrosModalRef);
  private componentFactoryResolver = inject(ComponentFactoryResolver);
  private injector = inject(Injector);
  private deviceDetector = inject(DeviceDetectorServiceOld);
  readonly #elementRef = inject(ElementRef<HTMLElement>);

  readonly id = createUniqueElementId();

  renderMethod: 'template' | 'component' | 'text' = 'component';
  // Component / Template / String to be displayed as modal
  content: ModalContent = this.modalRef.content;
  // data to be passed in as parameter
  context: any;
  addGradient = this.modalRef.settings.addGradient ?? false;
  isContentRendered = false;

  setSmallScreen = this.modalRef.settings.setSmallScreen ?? false;
  showMobileArrowBack = this.modalRef.settings.showMobileArrowBack;
  fullscreenOnMobile = this.modalRef.settings.fullscreenOnMobile;
  allowFocusAutocapture = this.modalRef.settings.allowFocusAutocapture;
  modalMobileHeightSettings: MobileHeightSettings = {
    heightPropertyName: 'max-height',
  };
  contentMobileHeightSettings: MobileHeightSettings = {
    heightPropertyName: 'max-height',
    offset: '7rem',
  };

  @ViewChild('componentSlot', { read: ViewContainerRef, static: true }) componentSlot!: ViewContainerRef;

  @Output() componentLoaded = new EventEmitter<ComponentRef<any>>();

  protected readonly newDesign = inject(NEW_DESIGN, { optional: true });

  constructor() {
    this.modalRef.overlay
      .keydownEvents()
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this.#onOverlayKeydown(event));
  }

  ngOnInit(): void {
    this.setMobileHeightSettings();

    if (typeof this.content === 'string') {
      this.renderMethod = 'text';
    }

    if (this.content instanceof TemplateRef) {
      this.renderMethod = 'template';
      // Bind data and methods to submit / cancel to template
      this.context = {
        data: this.modalRef.data,
        cancel: this.modalRef.cancel.bind(this.modalRef),
        accept: this.modalRef.submit.bind(this.modalRef),
      };
    }

    if (this.content instanceof Type) {
      this.renderMethod = 'component';
      const cmpFactory = this.componentFactoryResolver.resolveComponentFactory(this.content);
      const component = this.componentSlot.createComponent(cmpFactory, 0, this.injector);
      this.componentLoaded.emit(component as ComponentRef<any>);
    }

    this.isContentRendered = true;
  }

  /**
   * A layer inside the modal that already acted on Escape - a DevExtreme dropdown closing its list -
   * only marks the event as handled without stopping it, so the modal has to leave such events alone.
   */
  onEscapePress(event: Event): void {
    const isAutoRepeat = event instanceof KeyboardEvent && event.repeat;

    if (isAutoRepeat || event.defaultPrevented || !this.modalRef.settings.closeOnEscClick || !this.#isEscapeAddressedToModal(event)) {
      return;
    }

    this.modalRef.cancel();
    event.stopPropagation();
  }

  close(): void {
    this.modalRef.cancel();
  }

  smallScreenForDektop(): boolean {
    return document.documentElement.clientHeight < 1200 && this.setSmallScreen;
  }

  get isIOSOrIPadOS(): boolean {
    return this.deviceDetector.isIOSOrIPadOS;
  }

  get isFullscreen(): boolean {
    return this.modalRef.isFullscreen;
  }

  private setMobileHeightSettings(): void {
    this.modalMobileHeightSettings.offset = this.fullscreenOnMobile ? '' : '3.5rem';
    this.contentMobileHeightSettings.offset = this.showMobileArrowBack ? '7rem' : '';
  }

  #onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onEscapePress(event);
    }
  }

  /**
   * The overlay keyboard dispatcher delivers Escape regardless of where focus is, so the modal has
   * to decide what belongs to it: its own content and the page behind it, where modals opened
   * without focus autocapture leave focus. A layer above the modal - a DevExtreme popup or another
   * overlay - keeps handling Escape itself, and focus lost to the body belongs to nobody.
   */
  #isEscapeAddressedToModal(event: Event): boolean {
    const target = event.target;

    if (!(target instanceof Element)) {
      return false;
    }

    if (this.#elementRef.nativeElement.contains(target)) {
      return true;
    }

    if (target === document.body || target === document.documentElement) {
      return false;
    }

    return !target.closest('.cdk-overlay-container, .dx-overlay-wrapper');
  }
}
